import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { pmiTypeOptions, miCompanies, upfrontMiTypeOptions } from '../../../cmg-1003/models/lookups';
import { Cmg1003FileDataQuery, Cmg1003LoanQuery } from '../../../loan-state';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { LoanDataModel, PmiType } from '../../../disclosure/models/loan-information.model';
import { Observable, Subject, map, take, takeUntil } from 'rxjs';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Cmg1003ConstantsService } from '../../../cmg-1003/services/cmg-1003-constants.service';
import { MiQuoteResultComponent } from '../mi-quote-results/miquoteresult.component';
import { MiQuote, MiQuoteResponse } from '../../../disclosure/models/mi-quote.model';
import { MiQuoteService } from '../../services/mi-quote.service';
import { MessageService } from 'primeng/api';
import { SpinnerService } from '../../services/spinner.service';
import { CmgValidators } from '../../utils/required.validator';
import { CmgUiUtilities } from '../../../cmg-1003/components/common/cmg-ui-utils';
import { InputNumber } from 'primeng/inputnumber';
import { CmgPrimeNgInputNumberFocusDirective } from '../../directives/cmg-input-number-focus.directive';
import { CmgFormControlDomDirective } from '../../directives/cmg-form-control-dom.directive';
import { CmgValidatorMessagesComponent } from '../cmg-validator-messages/cmg-validator-messages.component';
import { CmgDecimalInputComponent } from '../../../../../../../libs/ui/src/lib/components/cmg-decimal-input/cmg-decimal-input.component';
import { CmgFormControlFactorResetDirective } from '../../directives/cmg-form-control-factor-reset.directive';
import { Select } from 'primeng/select';
import { AsyncPipe } from '@angular/common';
import { ButtonDirective } from 'primeng/button';
import { Toast } from 'primeng/toast';
import { CmgBusyIndicatorComponent } from '../../../../../../../libs/ui/src/lib/components/cmg-busy-indicator/cmg-busy-indicator.component';

@Component({
  selector: 'cmg-clear2-mi-quote',
  templateUrl: './miquote.component.html',
  styleUrls: ['./miquote.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    InputNumber,
    CmgPrimeNgInputNumberFocusDirective,
    CmgFormControlDomDirective,
    CmgValidatorMessagesComponent,
    CmgDecimalInputComponent,
    CmgFormControlFactorResetDirective,
    Select,
    ButtonDirective,
    Toast,
    CmgBusyIndicatorComponent,
    AsyncPipe,
  ],
})
export class MiQuoteComponent implements OnInit, OnDestroy {
  private readonly _loanQuery = inject(Cmg1003LoanQuery);
  private readonly _formBuilder = inject(UntypedFormBuilder);
  private readonly _cmg1003ConstantsService = inject(Cmg1003ConstantsService);
  private readonly _dialogService = inject(DialogService);
  private readonly _ref = inject(DynamicDialogRef);
  private readonly _config = inject(DynamicDialogConfig);
  private readonly _miQuoteService = inject(MiQuoteService);
  private readonly _fileDataQuery = inject(Cmg1003FileDataQuery);
  private readonly _messageService = inject(MessageService);
  private readonly _spinnerService = inject(SpinnerService);

  pmiTypeOptions = [...pmiTypeOptions];
  upfrontMiTypeOptions = upfrontMiTypeOptions;
  miCompanies = miCompanies;
  showPmiType$ = this._loanQuery.showPmiType$;
  loanInfo$: Observable<LoanDataModel> = this._loanQuery.selectLoanInfo();
  ltv$: Observable<number> = this._loanQuery.ltv$;
  dialogRef: DynamicDialogRef;
  miQuestResult: MiQuote = {} as MiQuote;
  companies: MiQuoteResponse = {} as MiQuoteResponse;
  coverage: number;
  modalData: ModalData | undefined = undefined;
  filedataId$: Observable<number>;
  fileDataId: number;
  upfrontMiFactor: number;
  monthlyMiAmount: number;
  mipffPercent: number;
  mipffFinancingCalcOption: number;
  showUFMIPF: boolean;
  showUFMIPP: boolean;
  submitDisable: boolean;

  isLoading$ = this._spinnerService.count$.pipe(map(Boolean));

  loadingMessage$ = this._spinnerService.message$;

  miQuoteFormGroup: UntypedFormGroup = this._formBuilder.group({
    ltv: [{ value: null, disabled: true }, [CmgValidators.required(0)]],
    coverage: [null, [Validators.required, CmgValidators.createRangeValidator(6, 35, 'Value must be between 6% and 35%')]],
    creditScore: [null, [Validators.required, CmgValidators.createRangeValidator(300, 850, 'Value must be between 300 and 850')]],
    pmiType: [PmiType.select, [Validators.required, this._createPmiTypeValidator()]],
    UFMIPF: [0],
    UFMIPP: [null],
  });

  private readonly _destroyed$ = new Subject<boolean>();

  ngOnInit(): void {
    const filterList: string[] = ['None', 'Lender Paid'];
    const filter = this._removeItem(filterList);
    this.pmiTypeOptions = filter;

    this.ltv$.pipe(takeUntil(this._destroyed$)).subscribe((x) => {
      this.miQuoteFormGroup.get('ltv').setValue(x);
    });

    this._fileDataQuery
      .select((t) => t.fileDataId)
      .pipe(takeUntil(this._destroyed$))
      .subscribe((Id) => {
        this.fileDataId = Id;
      });

    this._loanQuery
      .select((t) => t.upfrontMiFactor)
      .pipe(takeUntil(this._destroyed$))
      .subscribe((factor) => {
        this.upfrontMiFactor = factor;
      });

    this._loanQuery
      .select((t) => t.mipffFinancingCalcOption)
      .pipe(takeUntil(this._destroyed$))
      .subscribe((mfco) => {
        this.mipffFinancingCalcOption = mfco;
      });

    this._loanQuery
      .select((t) => t.mipffPercent)
      .pipe(takeUntil(this._destroyed$))
      .subscribe((mipff) => {
        this.mipffPercent = mipff;
      });

    this._loanQuery
      .select((t) => t.creditScoreMedian)
      .pipe(takeUntil(this._destroyed$))
      .subscribe((cs) => {
        if (cs !== null) {
          this.miQuoteFormGroup.get('creditScore').setValue(cs);
        }
      });

    this._loanQuery
      .select((t) => t.pmiType)
      .pipe(takeUntil(this._destroyed$))
      .subscribe((pmi) => {
        if (pmi !== null) {
          this.miQuoteFormGroup.get('pmiType').setValue(pmi);
          this._onPmiTypeChange(pmi);
        }
      });

    this.miQuoteFormGroup
      .get('pmiType')
      .valueChanges.pipe(takeUntil(this._destroyed$))
      .subscribe((x) => {
        this._onPmiTypeChange(x);
      });

    // When MI Quote Results is cancelled, repopulate prevous Quote values
    if (this._config?.data?.miQuoteData) {
      this.miQuoteFormGroup.patchValue(this._config?.data?.miQuoteData);
    }
  }

  ngOnDestroy(): void {
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }

  onGetMIQuoteResult() {
    CmgUiUtilities.markFormDirtyTouched(this.miQuoteFormGroup);
    if (this.miQuoteFormGroup.invalid) {
      return;
    }

    this.miQuestResult.coveragePercentage = this.miQuoteFormGroup.value['coverage'];
    this.miQuestResult.estimatedCreditScore = this.miQuoteFormGroup.value['creditScore'];
    this.miQuestResult.fileDataId = this.fileDataId;
    this.miQuestResult.miMethod = this.miQuoteFormGroup.value['pmiType'];
    this.miQuestResult.mipffPerc = this.miQuoteFormGroup.value['UFMIPP'] ? this.miQuoteFormGroup.value['UFMIPP'] : this.mipffPercent;
    this.miQuestResult.mipffFinanced = this.miQuoteFormGroup.value['UFMIPF'];

    if (this.miQuestResult.mipffPerc === undefined) {
      this.miQuestResult.mipffPerc = 0;
    }

    this._spinnerService.show();

    this._miQuoteService
      .getMIQuotes(this.miQuestResult)
      .pipe(take(1))
      .subscribe({
        next: (miQuote: MiQuoteResponse) => {
          this.companies = miQuote;

          if (this.companies === null) {
            this._messageService.add({ severity: 'error', summary: 'Error', detail: 'No results.' });
          } else if (miQuote.errors?.length > 0) {
            this._messageService.add({ severity: 'error', summary: 'Error', detail: miQuote.errors[0] });
          } else {
            this.dialogRef = this._openMIQuoteResults(MiQuoteResultComponent);
          }
        },
        error: () => {
          this._messageService.add({
            severity: 'error',
            summary: 'Failed to Get Mi Quote',
            detail: 'No Data Found',
            life: 5000,
          });
          this._spinnerService.hide();
        },
        complete: () => {
          this._spinnerService.hide();
        },
      });
  }

  clear() {
    this._ref.close();
  }

  selectAll() {
    if (this.miQuoteFormGroup.controls.selectDeselectCheckbox.value.length > 0) {
      this.miQuoteFormGroup.controls.miCompaniesList.setValue(this.miCompanies);
    } else {
      this.miQuoteFormGroup.controls.miCompaniesList.setValue([]);
    }
  }

  private _onPmiTypeChange(type: PmiType): void {
    if (type === PmiType.standard) {
      this.showUFMIPF = false;
      this.showUFMIPP = false;
      this.submitDisable = true;
      this.miQuoteFormGroup.get('pmiType').markAsDirty();
    } else if ([PmiType.singlePremRefund, PmiType.singlePremNonRefund].includes(type)) {
      this.showUFMIPF = true;
      this.showUFMIPP = false;
      this.submitDisable = false;
      this.miQuoteFormGroup.get('UFMIPP').setValidators(null);
    } else if (type === PmiType.splitPremNonRefund) {
      this.showUFMIPF = true;
      this.showUFMIPP = true;
      this.submitDisable = false;
      this.miQuoteFormGroup.get('UFMIPP').setValidators([CmgValidators.required(0)]);
    } else {
      this.showUFMIPF = false;
      this.showUFMIPP = false;
      this.submitDisable = false;
      this.miQuoteFormGroup.get('UFMIPF').setValidators(null);
      this.miQuoteFormGroup.get('UFMIPP').setValidators(null);
      this.miQuoteFormGroup.get('UFMIPF').setValue(0);
      this.miQuoteFormGroup.get('UFMIPP').setValue(null);
    }

    this.miQuoteFormGroup.get('UFMIPF').updateValueAndValidity({ emitEvent: false });
    this.miQuoteFormGroup.get('UFMIPP').updateValueAndValidity({ emitEvent: false });
  }

  private _createPmiTypeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value === PmiType.standard) {
        return { cmgValidationError: 'PMI Type Standard Annual is not supported for MI vendors interfaces.' };
      } else if (control.value === PmiType.none || control.value === PmiType.lenderPaid) {
        return { required: true };
      }
      return null;
    };
  }

  private _removeItem(company) {
    this.pmiTypeOptions.forEach((item, index) => {
      if (company.includes(item.name)) {
        this.pmiTypeOptions.splice(index, 1);
      }
    });
    return this.pmiTypeOptions;
  }

  private _openMIQuoteResults(component: any): DynamicDialogRef {
    this._ref.close(true);
    return this._dialogService.open(component, {
      draggable: true,
      keepInViewport: true,
      data: {
        companies: this.companies,
        pmiType: this.miQuoteFormGroup.value['pmiType'],
        miQuoteData: this.miQuoteFormGroup.getRawValue(),
      },
      styleClass: 'mi-results-modal',
      contentStyle: { display: 'inline-block', overflow: 'auto' },
      baseZIndex: this._cmg1003ConstantsService.modalZIndex,
    });
  }
}

type ModalData = {
  companies: MiQuoteResponse;
};
